import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, User } from 'lucide-react';
import TeamsHero from '../components/teams/TeamsHero';
import TeamsPainPoints from '../components/teams/TeamsPainPoints';
import TeamsHowItWorks from '../components/teams/TeamsHowItWorks';
import TeamsValueProp from '../components/teams/TeamsValueProp';
import TeamsTrust from '../components/teams/TeamsTrust';
import TeamsPricing from '../components/teams/TeamsPricing';

const TeamsLandingPage = () => {
  const navigate = useNavigate();

  const handleStartTrial = () => {
    // Navigate to dedicated team signup flow
    navigate('/teams/signup');
  };

  const handleLearnMore = () => {
    // Smooth scroll to how it works section
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleContactUs = () => {
    // Open email for enterprise contact
    window.location.href = 'mailto:teams@30dayskills.co.uk?subject=Team%20Pro%20Inquiry';
  };

  const handleIndividuals = () => {
    navigate('/');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="teams-landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          {/* Logo */}
          <div 
            className="flex items-center gap-2 cursor-pointer" 
            onClick={() => navigate('/')}
            data-testid="teams-logo"
          >
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">30</span>
            </div>
            <span className="text-white font-semibold hidden sm:block">Day Skills</span>
          </div>

          {/* Nav links */}
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleIndividuals}
              className="text-slate-400 hover:text-white"
              data-testid="for-individuals-btn"
            >
              <User className="w-4 h-4 mr-2" />
              For Individuals
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleLogin}
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="login-btn"
            >
              Log In
            </Button>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main>
        <TeamsHero 
          onStartTrial={handleStartTrial} 
          onLearnMore={handleLearnMore} 
        />
        <TeamsPainPoints />
        <TeamsHowItWorks />
        <TeamsValueProp />
        <TeamsTrust />
        <TeamsPricing 
          onStartTrial={handleStartTrial}
          onContactUs={handleContactUs}
        />

        {/* Final CTA Section */}
        <section className="py-24 bg-gradient-to-b from-slate-950 to-slate-900" data-testid="teams-final-cta">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to see what's really happening?
            </h2>
            <p className="text-slate-400 text-lg mb-8">
              Start your 28-day team trial today. No credit card required.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg"
                onClick={handleStartTrial}
                className="w-full sm:w-auto px-8 py-6 text-lg bg-blue-600 hover:bg-blue-500"
                data-testid="final-cta-btn"
              >
                Start Team Trial
              </Button>
              <Button 
                variant="outline"
                size="lg"
                onClick={handleContactUs}
                className="w-full sm:w-auto px-8 py-6 text-lg border-slate-700 text-slate-300 hover:bg-slate-800"
                data-testid="contact-sales-btn"
              >
                Talk to Sales
              </Button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 bg-slate-950 border-t border-slate-800">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-slate-500 text-sm">
                © 2026 30 Day Skills. All rights reserved.
              </p>
              <div className="flex items-center gap-6">
                <a href="/privacy" className="text-slate-500 hover:text-slate-400 text-sm">Privacy</a>
                <a href="/terms" className="text-slate-500 hover:text-slate-400 text-sm">Terms</a>
                <a href="mailto:support@30dayskills.co.uk" className="text-slate-500 hover:text-slate-400 text-sm">Contact</a>
              </div>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default TeamsLandingPage;
